import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
  inject,
  input,
  output,
} from '@angular/core';

export interface ColumnResizeEvent {
  /** Identifier passed to [jldColumnResize] — a column name, not an index. */
  column: string;
  /** Settled width in px. */
  width: number;
}

/**
 * Column resizing for the job-log-detail table.
 *
 * Separate from the shared `appColumnResize` because that directive sizes the
 * `<th>` itself, which cannot work here: the header row and each coordinator
 * group's data rows are separate `<table>` elements, so widths have to be
 * driven from the shared `<colgroup>` rather than from a single cell.
 *
 * While dragging, the width is written to a `--jld-col-<index>-width` custom
 * property on the common `.jld-table-wrapper` ancestor, which keeps every table
 * — including group tables not yet rendered — in sync live. On mouseup the
 * variable is dropped and the final width is emitted so the host component can
 * store it per column *name*; index-keyed storage would mis-apply widths as
 * soon as a column is added or removed.
 */
@Directive({
  selector: '[jldColumnResize]',
  standalone: false,
})
export class JldColumnResizeDirective implements OnInit, OnDestroy {
  /** Column this header cell belongs to; echoed back in columnResized. */
  readonly column = input.required<string>({ alias: 'jldColumnResize' });

  /** Emitted once per drag, on mouseup. */
  readonly columnResized = output<ColumnResizeEvent>();

  private static readonly MIN_WIDTH = 80;
  private static readonly HANDLE_CLASS = 'jld-resize-handle';
  private static readonly HANDLE_ACTIVE_CLASS = 'jld-resize-handle--active';

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);

  private resizeHandle!: HTMLElement;
  private isResizing = false;
  private hasDragged = false;
  private startX = 0;
  private startWidth = 0;
  /** Teardown callbacks for every listener registered by this directive. */
  private unlisteners: (() => void)[] = [];
  private dragUnlisteners: (() => void)[] = [];

  ngOnInit(): void {
    this.createResizeHandle();
  }

  ngOnDestroy(): void {
    this.stopDrag();
    this.unlisteners.forEach(un => un());
    this.unlisteners = [];
    this.resizeHandle?.remove();
  }

  // ── Handle ───────────────────────────────────────────────────────────────

  private createResizeHandle(): void {
    // Geometry and hover styling live in the component stylesheet under
    // .jld-resize-handle; only the drag state is toggled from here.
    this.resizeHandle = this.renderer.createElement('div');
    this.renderer.addClass(this.resizeHandle, JldColumnResizeDirective.HANDLE_CLASS);

    const host = this.el.nativeElement;
    // The header <th> is `position: sticky`, which already establishes a
    // containing block for the absolutely positioned handle. Only fall back to
    // `relative` when the host is static — setting it unconditionally would
    // unstick the header row on scroll.
    if (this.getHostPosition() === 'static') {
      this.renderer.setStyle(host, 'position', 'relative');
    }
    this.renderer.setStyle(host, 'user-select', 'none');
    this.renderer.appendChild(host, this.resizeHandle);

    this.unlisteners.push(
      this.renderer.listen(this.resizeHandle, 'mousedown', (e: MouseEvent) => this.onMouseDown(e)),
      // A click on the handle — drag or not — must never reach the header's
      // sort handler. mousedown/mouseup are stopped separately, but the click
      // that the browser synthesises afterwards bubbles from the handle to the
      // <th> on its own and has to be stopped here.
      this.renderer.listen(this.resizeHandle, 'click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }),
    );
  }

  private getHostPosition(): string {
    return this.document.defaultView?.getComputedStyle(this.el.nativeElement).position ?? 'static';
  }

  // ── Live width, shared by every table under .jld-table-wrapper ────────────

  private getColumnIndex(): number {
    const th = this.el.nativeElement;
    return th.parentElement ? Array.from(th.parentElement.children).indexOf(th) : -1;
  }

  private getWidthVar(): string {
    return `--jld-col-${this.getColumnIndex()}-width`;
  }

  private setColumnWidthVar(width: number): void {
    const wrapper = this.el.nativeElement.closest('.jld-table-wrapper');
    if (!wrapper) return;
    // Renderer2 only routes through style.setProperty() when DashCase is set;
    // without the flag a custom property assignment is silently dropped.
    this.renderer.setStyle(wrapper, this.getWidthVar(), `${width}px`, RendererStyleFlags2.DashCase);
  }

  private clearColumnWidthVar(): void {
    const wrapper = this.el.nativeElement.closest('.jld-table-wrapper');
    if (!wrapper) return;
    this.renderer.removeStyle(wrapper, this.getWidthVar(), RendererStyleFlags2.DashCase);
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  private onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.hasDragged = false;
    this.startX = event.pageX;
    this.startWidth = this.el.nativeElement.offsetWidth;

    this.renderer.addClass(this.resizeHandle, JldColumnResizeDirective.HANDLE_ACTIVE_CLASS);
    this.setDragCursor(true);

    // mousemove fires continuously and only mutates a CSS custom property, so
    // it is kept out of the zone; the single emit on mouseup re-enters it.
    this.zone.runOutsideAngular(() => {
      this.dragUnlisteners = [
        this.renderer.listen('document', 'mousemove', (e: MouseEvent) => this.onMouseMove(e)),
        this.renderer.listen('document', 'mouseup', (e: MouseEvent) => this.onMouseUp(e)),
      ];
    });
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    this.hasDragged = true;
    this.setColumnWidthVar(
      Math.max(JldColumnResizeDirective.MIN_WIDTH, this.startWidth + (event.pageX - this.startX)),
    );
  }

  private onMouseUp(event: MouseEvent): void {
    if (!this.isResizing) return;

    event.preventDefault();
    event.stopPropagation();

    const width = this.el.nativeElement.offsetWidth;
    const dragged = this.hasDragged;
    this.stopDrag();

    // A drag that ends outside the handle still produces a click on the <th>,
    // which the handle's own click listener never sees — swallow it once, in
    // the capture phase, before the sort handler runs.
    this.suppressNextHostClick();

    if (!dragged) return;

    // Hand the width to the component, which keys it by column name, and drop
    // the index-keyed variable so the two can never disagree.
    this.clearColumnWidthVar();
    this.zone.run(() => this.columnResized.emit({ column: this.column(), width }));
  }

  private suppressNextHostClick(): void {
    const host = this.el.nativeElement;
    const preventClick = (e: MouseEvent) => {
      e.stopPropagation();
      host.removeEventListener('click', preventClick, true);
    };
    host.addEventListener('click', preventClick, true);
    // No click follows a mouseup outside the host — drop the listener next tick
    // so it cannot swallow a genuine header click later on.
    setTimeout(() => host.removeEventListener('click', preventClick, true));
  }

  private stopDrag(): void {
    this.dragUnlisteners.forEach(un => un());
    this.dragUnlisteners = [];
    if (!this.isResizing) return;

    this.isResizing = false;
    this.renderer.removeClass(this.resizeHandle, JldColumnResizeDirective.HANDLE_ACTIVE_CLASS);
    this.setDragCursor(false);
  }

  private setDragCursor(dragging: boolean): void {
    const body = this.document.body;
    if (dragging) {
      this.renderer.addClass(body, 'resizing-column');
      this.renderer.setStyle(body, 'cursor', 'col-resize');
      this.renderer.setStyle(body, 'user-select', 'none');
    } else {
      this.renderer.removeClass(body, 'resizing-column');
      this.renderer.removeStyle(body, 'cursor');
      this.renderer.removeStyle(body, 'user-select');
    }
  }
}
